import React, { useEffect, useRef, useState } from 'react';
import ProductSelectionSupplierModal from '../../components/ProductSelectionSupplierModal';
import SearchIcon from '../../images/SearchIcon';
import CloseIcon from '../../images/CloseIcon';
import WhiteCheckIcon from '../../images/WhiteCheckIcon';
import { useInfiniteScroll } from '../../hook/useInfiniteScroll';
import { getListProductWithSupplierReturn } from '../../services/items.services';
import { createSupplierReturn, SupplierReturnDetail, SupplierReturnPayload, updateSupplierReturn } from '../../services/supplierReturn.service';
import { getDetailSupplierReturn } from '../../services/supplier.service';
import { webLoadingFalse, webLoadingTrue, webNavigate } from '../../services/navigate.service';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';

const PAGE_SIZE = 30;
const initialFilter = { searchValue: '', page: 1, searchValueProduct: '', initLoad: false };

interface SupplierReturnFlowProps {
  open: boolean;
  onClose: () => void;
  order: any;
  language: any;
}

/** Return to supplier from a received order: pick its lines, then the return is created or updated. */
const SupplierReturnFlow: React.FC<SupplierReturnFlowProps> = ({ open, onClose, order, language }) => {
  const preLanguage = localStorage.getItem('language');
  const id: string = order?.id ?? '';
  const [listProductsReturn, setListProductsReturn] = useState<any[]>([]);
  // The saved return's lines (null until loaded); a return-picker untick lives on the line's checkProduct.
  const [returnLines, setReturnLines] = useState<any[] | null>(null);
  // Rows ticked in the return picker that are not saved lines yet.
  const [listProductsSelectReturn, setListProductsSelectReturn] = useState<any[]>([]);
  const [searchUOMReturn, setSearchUOMReturn] = useState<any>();
  const [filterReturn, setFilterReturn] = useState<any>(initialFilter);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const scrollRefReturn = useRef<HTMLDivElement | null>(null);
  const hasLoadedPageReturnRef = useRef(new Set<number>());
  const returnSeqRef = useRef(0);
  const startedRef = useRef(false);

  // Ticked = saved lines not unticked in this session + explicit ticks, keyed by product id.
  const isPickedReturn = (productId: string) =>
    (returnLines ?? []).some((line: any) => line.productId === productId && line.checkProduct !== false) ||
    listProductsSelectReturn.some((p: any) => p.productId === productId);

  const fetchAndAppendProductListReturn = async (filter: any, isAppend: boolean, returnSupplierId: any) => {
    const seq = returnSeqRef.current;
    try {
      setLoading(true);
      const response: any = await getListProductWithSupplierReturn(filter.page, PAGE_SIZE, filter.searchValue, returnSupplierId, id);
      // A newer search owns the list and its paging.
      if (seq !== returnSeqRef.current) return;
      const fetchedProducts: any = response.data.data.data;
      const updatedProducts: any[] = fetchedProducts.map((value: any) => {
        const uomSelect = value?.product?.productUOMs.find((v: any) => v?.id === value.productUOMId);
        if (uomSelect) {
          setSearchUOMReturn((prev: any) => ({ ...(prev || {}), [value?.product.id]: uomSelect?.uom?.uomName || '/' }));
        }
        return {
          // Once the saved lines are loaded, ticks mirror them and this session's picks; the API knows only saved ones.
          checkProduct: returnLines ? isPickedReturn(value?.product.id) : value?.checkProduct,
          productId: value?.product.id,
          groupName: value?.product.productGroup.groupName,
          productName: value?.product.productName,
          sku: value?.product.sku,
          uomName: uomSelect?.uom?.uomName || '/',
          productUOMs: value?.product?.productUOMs,
          productUOMId: value?.productUOMId,
          cost: value?.cost,
          lineCost: value?.lineCost,
          productPrices: value?.product?.productPrices,
          originalOrderDetailId: value?.id,
          qtyReturned: value?.qtyReturned,
        };
      });
      if (isAppend) {
        // An order line already shown is never added twice.
        setListProductsReturn((prev) => [...prev, ...updatedProducts.filter((row) => !prev.some((p) => p.originalOrderDetailId === row.originalOrderDetailId))]);
      } else {
        setListProductsReturn(updatedProducts);
      }
      setFilterReturn((prev: any) => ({ ...prev, page: filter.page + 1, searchValue: filter.searchValue }));
      setHasMore(fetchedProducts.length >= PAGE_SIZE);
    } catch (err) {
      console.error('Error fetching product list return:', err);
      if (seq === returnSeqRef.current) hasLoadedPageReturnRef.current.delete(filter.page);
    } finally {
      if (seq === returnSeqRef.current) setLoading(false);
    }
  };

  const onLoadMoreReturn = async (returnSupplierId?: any) => {
    if (hasLoadedPageReturnRef.current.has(filterReturn.page)) return;
    hasLoadedPageReturnRef.current.add(filterReturn.page);
    setLoading(true);
    // The scroll loader passes no id; without it the API orders rows differently and pages overlap.
    await fetchAndAppendProductListReturn(filterReturn, true, returnSupplierId ?? order?.supplierReturn?.id);
  };

  const { setLoaderRef: setLoaderRefReturn } = useInfiniteScroll({
    hasMore,
    loading,
    onLoadMore: onLoadMoreReturn,
    scrollRootRef: scrollRefReturn,
  });

  // The saved return's lines are what Return Now keeps; the picker only unticks them or adds others.
  const loadReturnLines = async (supplierReturnId?: string) => {
    const response: any = supplierReturnId ? await getDetailSupplierReturn(supplierReturnId) : null;
    const lines: any[] = (response?.data?.data?.supplierReturnDetails ?? []).map((line: any) => ({ ...line, checkProduct: true }));
    setReturnLines(lines);
    return lines;
  };

  // Loaded on first open only, so an order page that never returns anything asks for none of it.
  useEffect(() => {
    if (!open || startedRef.current || !id) return;
    startedRef.current = true;
    onLoadMoreReturn(order?.supplierReturn?.id);
    loadReturnLines(order?.supplierReturn?.id).catch((err) => console.error('Error loading return lines:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  // Keyed by product id, so a tick survives paging and searches; nothing is sent until Return Now.
  const handleSelectProductReturn = (value: any) => {
    const checked = !value.checkProduct;
    if ((returnLines ?? []).some((line: any) => line.productId === value.productId)) {
      setReturnLines((prev) => (prev ? prev.map((line: any) => (line.productId === value.productId ? { ...line, checkProduct: checked } : line)) : prev));
    } else {
      setListProductsSelectReturn((prev) => [...prev.filter((p: any) => p.productId !== value.productId), ...(checked ? [{ ...value, checkProduct: true }] : [])]);
    }
    setListProductsReturn((prev) => prev.map((product: any) => (product.productId === value.productId ? { ...product, checkProduct: checked } : product)));
  };

  const handleSelectUOMReturn = (value: any) => {
    setListProductsReturn(value);
    // An explicit tick keeps the UOM picked on its row, which Return Now then sends.
    setListProductsSelectReturn((prev) =>
      prev.map((p: any) => {
        const row = value.find((v: any) => v.productId === p.productId);
        return row ? { ...row, checkProduct: true } : p;
      }),
    );
  };

  // Discard the session: saved lines back to ticked, explicit ticks dropped.
  const handleCancelAddProductReturn = () => {
    const lineIds = new Set((returnLines ?? []).map((line: any) => line.productId));
    setReturnLines((prev) => (prev ? prev.map((line: any) => ({ ...line, checkProduct: true })) : prev));
    setListProductsSelectReturn([]);
    setListProductsReturn((prev) => prev.map((p: any) => ({ ...p, checkProduct: lineIds.has(p.productId) })));
    onClose();
  };

  const handleAddProductReturn = async () => {
    // The API soft-deletes every saved line left out, so the saved lines, not the picker view, are the source.
    let lines = returnLines;
    if (!lines) {
      try {
        lines = await loadReturnLines(order?.supplierReturn?.id);
      } catch (error: any) {
        showApiError(error, { language });
        return;
      }
    }
    const lineIds = new Set(lines.map((line: any) => line.productId));
    // Kept lines go back exactly as saved; an unmatched UOM is left out so the API keeps the saved one.
    const keptDetails = lines
      .filter((line: any) => line.checkProduct !== false)
      .map(
        (line: any): SupplierReturnDetail => ({
          productId: line.productId,
          originalOrderDetailId: line.originalOrderDetailId,
          qtyReturned: Number(line.qtyReturned) || 0,
          unitCost: Number(line.unitCost) || 0,
          lineCost: Number(line.lineTotal) || 0,
          taxRateId: line.taxRateId ?? '',
          productUOMId: line.product?.productUOMs?.find((u: any) => u.UOMId === line.uomId)?.id,
        }),
      );
    // New lines = explicit ticks of products that are not saved lines.
    const addedDetails = listProductsSelectReturn
      .filter((item: any) => !lineIds.has(item.productId))
      .map((item: any): SupplierReturnDetail => {
        const conversionFactor = item.productUOMs.find((v: any) => v.id === item.productUOMId)?.uom?.conversionFactor;
        return {
          productId: item.productId,
          originalOrderDetailId: item.originalOrderDetailId,
          qtyReturned: Number(item.qtyReturned) || 0,
          unitCost: Number(item.cost) / Number(conversionFactor) || 0,
          lineCost: +item.lineCost,
          taxRateId: item?.productPrices?.[0]?.tax?.id ?? '',
          productUOMId: item.productUOMId,
        };
      });
    const payload: SupplierReturnPayload = {
      locationId: order?.location?.id ?? '',
      supplierId: order?.clientId ?? '',
      orderId: id,
      statusId: Number(1),
      reason: '7',
      supplierReturnDetails: [...keptDetails, ...addedDetails],
    };

    onClose();
    webLoadingTrue();
    try {
      const response: any = order?.supplierReturn?.id ? await updateSupplierReturn(payload, order.supplierReturn.id) : await createSupplierReturn(payload);
      if (response) {
        webNavigate('/' + preLanguage + '/supplierReturn');
        showToast({
          message: order?.supplierReturn?.id ? language?.['Supplier Return Updated !'] ?? 'Supplier Return Updated !' : language?.['Supplier Return Created !'] ?? 'Supplier Return Created !',
          type: 'success',
          duration: 20000,
        });
      }
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      webLoadingFalse();
    }
  };

  // A search starts at page 1 of its term and drops older loads; scrolling then resumes at page 2.
  const handleSubmitSearchProductReturn = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    returnSeqRef.current += 1;
    hasLoadedPageReturnRef.current = new Set([1]);
    await fetchAndAppendProductListReturn({ ...filterReturn, page: 1 }, false, order?.supplierReturn?.id);
  };

  return (
    <ProductSelectionSupplierModal
      showPopup={open}
      onClose={handleCancelAddProductReturn}
      products={listProductsReturn}
      filter={filterReturn}
      searchUOMBase={searchUOMReturn}
      onFilterChange={setFilterReturn}
      onSearchSubmit={handleSubmitSearchProductReturn}
      onSelectProduct={handleSelectProductReturn}
      onSelectUOM={handleSelectUOMReturn}
      onCancel={handleCancelAddProductReturn}
      onSave={handleAddProductReturn}
      loading={loading}
      language={language}
      scrollRef={scrollRefReturn}
      loaderRef={setLoaderRefReturn}
      SearchIcon={SearchIcon}
      CloseIcon={CloseIcon}
      WhiteCheckIcon={WhiteCheckIcon}
    />
  );
};

export default SupplierReturnFlow;
